import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, formValueSelector, reduxForm } from 'redux-form';
import {Alert, Col, Row, ControlLabel, FormGroup, Grid} from 'react-bootstrap';
import getIncludedResource from '../../../../helpers/getIncludedResource';
import {
  Button,
  HorizontalFormControl,
  Modal,
  NameFields,
} from '../../../../common/components';
import { get, set } from 'lodash';
import UploadCropComponent from '../../../../common/components/Upload/UploadCrop';
import { triggerFileUpload } from '../../../../common/components/Upload/UploadRedux';
import defaultAvatar from '../../../../images/avatars/user_default.png';
import InfoLabel from '../../../../common/components/InfoLabel/InfoLabel';
import { hasPermission } from '../../../auth/redux/authActions';
import './SettingsForm.scss';

const FORM_NAME = 'mySettings';

const validate = (values) => {
  const errors = {};

  if (!get(values, 'data.attributes.email')) {
    set(errors, 'data.attributes.email', 'Required');
  } else if (
    !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,40}$/i.test(
      get(values, 'data.attributes.email'),
    )
  ) {
    set(errors, 'data.attributes.email', 'Invalid email address');
  }

  if (
    get(values, 'data.attributes.password') &&
    get(values, 'data.attributes.password.length', '') < 8
  ) {
    set(errors, 'data.attributes.password', 'Must be 8 characters or more');
  }

  if (
    get(values, 'data.attributes.password') &&
    !/[a-z]/.test(get(values, 'data.attributes.password'))
  ) {
    set(
      errors,
      'data.attributes.password',
      'Must contain at least one lowercase character',
    );
  }

  if (
    get(values, 'data.attributes.password') &&
    !/[A-Z]/.test(get(values, 'data.attributes.password'))
  ) {
    set(
      errors,
      'data.attributes.password',
      'Must contain at least one uppercase character',
    );
  }

  if (
    get(values, 'data.attributes.password') &&
    !/[0-9]/.test(get(values, 'data.attributes.password'))
  ) {
    set(errors, 'data.attributes.password', 'Must contain at least one number');
  }

  if (
    get(values, 'data.attributes.password') &&
    get(values, 'data.attributes.password_confirmation') &&
    get(values, 'data.attributes.password_confirmation') !==
      get(values, 'data.attributes.password')
  ) {
    set(
      errors,
      'data.attributes.password_confirmation',
      'Confirmation does not match password',
    );
  }

  return errors;
};

export class MySettingsForm extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    resource: PropTypes.object.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    submitting: PropTypes.bool.isRequired,
  };

  handleUpload() {
    const { dispatch } = this.props;

    dispatch(triggerFileUpload());
  }

  handleUploadComplete(data) {
    const { dispatch, change } = this.props;

    dispatch(change('data.relationships.avatar.data', data.data));
  }

  handleOnHide() {
    const { dispatch, reset } = this.props;

    dispatch(reset());
  }

  render() {
    const { error, resource, formValues, submitting, handleSubmit } = this.props;
    const avatar = getIncludedResource(resource.data, resource.included, 'avatar');

    let avatarSrc = '';

    if (get(formValues, 'data.relationships.avatar.data.attributes')) {
      avatarSrc = formValues.data.relationships.avatar.data.attributes.url;
    } else if (avatar.data.attributes) {
      avatarSrc = avatar.data.attributes.url;
    } else {
      avatarSrc = defaultAvatar;
    }

    const modalFooter = (
      <div>
        <Button
          type="submit"
          bsStyle="primary settings-button"
          className="pull-right"
          disabled={submitting}
        >
          {submitting ? <i className="fa fa-cog fa-spin" /> : ''} Save Settings
        </Button>
        <Button
          type="button"
          bsStyle="primary settings-button avatar-button"
          className="pull-right"
          disabled={submitting}
          handleClick={::this.handleUpload}
        >
          {submitting ? (
            <i className="fa fa-cog fa-spin" />
          ) : (
            <i className="fa fa-user" />
          )}{' '}
          Upload Avatar
        </Button>
      </div>
    );

    return (
      <Modal
        {...this.props}
        name="settings"
        title="Personal Settings"
        close={true}
        onHide={::this.handleOnHide}
        footer={modalFooter}
        handleSubmit={handleSubmit}
      >
        <Row>
          <Col sm={12}>
            {error && (
              <Alert bsStyle="danger">
                <p>{error}</p>
              </Alert>
            )}
          </Col>
        </Row>

        {hasPermission('user.edit_own') ? (
          <div>
            <NameFields
              baseName="data.attributes"
              label="Name"
              fieldComponent={Field}
              disabled={submitting}
              labelSize={2}
            />
            <Field
              name="data.attributes.email"
              type="email"
              label="Email"
              placeholder="Email"
              component={HorizontalFormControl}
              disabled={submitting}
              labelSize={2}
            />
          </div>
        ) : (
          <div>
            <InfoLabel label="Name" value={resource.data.attributes.full_name} />
            <InfoLabel label="Email" value={resource.data.attributes.email} />
          </div>
        )}

        <FormGroup>
          <Col componentClass={ControlLabel} sm={2}>
            Avatar:
          </Col>
          <Col sm={10}>
            {/* This should get an avatar component */}
            <img
              src={avatarSrc}
              className="avatar img-circle"
              style={{ maxWidth: 64, maxHeight: 64 }}
            />
          </Col>
        </FormGroup>

        <div className="hr transparent" />

        <h5>Update your password (leave blank unless you intend to change)</h5>
        <Field
          name="data.attributes.current_password"
          type="password"
          label="Current Password"
          labelSize={3}
          placeholder="Current Password"
          disabled={submitting}
          component={HorizontalFormControl}
        />
        <Field
          name="data.attributes.password"
          type="password"
          label="Password"
          labelSize={3}
          placeholder="Password"
          component={HorizontalFormControl}
          disabled={submitting}
        />
        <Field
          name="data.attributes.password_confirmation"
          type="password"
          label="Confirm Password"
          labelSize={3}
          placeholder="Password"
          component={HorizontalFormControl}
          disabled={submitting}
        />

        <UploadCropComponent
          onComplete={::this.handleUploadComplete}
          width={200}
          height={200}
          bsSize="sm"
          circle={true}
        />
      </Modal>
    );
  }
}

const form = reduxForm({
  form: FORM_NAME,
  validate,
})(MySettingsForm);

const selector = formValueSelector(FORM_NAME);
export default connect((state, props) => {
  const values = selector(state, 'data.id', 'data.relationships.avatar.data');
  return {
    initialValues: props.resource,
    formValues: values,
    files: state.upload.files,
  };
})(form);
